import { useMemo, useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Coins, ArrowRight, Clock, Zap, Gamepad2, ClipboardList, Users, CircleDollarSign, Shield as ShieldIcon, PartyPopper, Unlock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useMe } from '../hooks/useProfile';
import { useRankedSurveys } from '../hooks/useSurveys';
import { useGames } from '../hooks/useGames';
import { usePointsLedger, useRedemptions } from '../hooks/useRedemptions';
import { usePendingSummary } from '../hooks/usePendingPoints';
import { getAvailablePoints, pointsToUsd, daysUntil } from '../lib/points';
import NavShell from '../components/NavShell';
import { ElevatedPayoutExtras } from '../components/ElevatedPayoutExtras';
import StreakWidget from '../components/StreakWidget';
import ProfileScoreCard from '../components/ProfileScoreCard';
import SwipeDashboardSection from '../components/SwipeDashboardSection';
import SurveyStartModal from '../components/SurveyStartModal';
import CustomQuestionPrescreenModal from '../components/CustomQuestionPrescreenModal';
import WelcomeSurveyModal from '../components/WelcomeSurveyModal';
import WelcomeSurveyBanner from '../components/WelcomeSurveyBanner';
import { fetchWelcomeSurvey, fetchQualifyingCount } from '../lib/api';
import { startManualSurvey, type ManualStartResponse } from '../lib/manualClick';
import OfferDetailModal, { StoreQRModal, isMobileUserAgent, gameToDetailItem } from '../components/OfferDetailModal';
import { scoreSurveys, getLoi, getMatchTier, matchTierPillClasses } from '../lib/surveyRanking';
import { ledgerLabel } from '@swipeopinion/shared';
import type { RankedSurvey, DisplayGame } from '@swipeopinion/shared';

// Lucide alias kept for the existing Quick Links section below the survey cards.
const ClipboardIcon = ClipboardList;

function getDisplayName(me: ReturnType<typeof useMe>['data']): string | null {
  return me?.display_name ?? me?.member?.display_name ?? null;
}

// ledgerLabel now lives in @swipeopinion/shared so the Dashboard, Profile, and
// the future all-earnings history render one consistent source_type → label map.
// Re-exported here for existing importers (and the dashboardLedgerLabel test).
export { ledgerLabel };

export default function DashboardPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const { data: surveys = [], refetch } = useRankedSurveys();
  const { games: gamesList } = useGames();
  const { data: ledger } = usePointsLedger(1);


  const [selectedSurvey, setSelectedSurvey] = useState<RankedSurvey | null>(null);
  // Phase 2.5 — manual-exchange custom-question prescreen state.
  const [manualPrescreen, setManualPrescreen] = useState<
    | { sessionId: string; surveyId: string; customQuestion: import('../lib/manualClick').CustomQuestionPayload }
    | null
  >(null);
  const [selectedGame, setSelectedGame] = useState<DisplayGame | null>(null);
  const [qrGame, setQrGame] = useState<DisplayGame | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Welcome survey — guaranteed first win (B2). Same shared banner + modal +
  // ['welcome-survey'] query as /surveys; the cache key is shared so no extra
  // fetch when both surfaces are visited. Gated on the /me flag.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeEligible = me?.welcome_survey_completed === false;
  const { data: welcome } = useQuery({
    queryKey: ['welcome-survey'],
    queryFn: fetchWelcomeSurvey,
    enabled: welcomeEligible,
    staleTime: 5 * 60 * 1000,
  });
  const welcomeAvailable = welcomeEligible && welcome?.available === true;

  // Accurate, UNCAPPED "surveys ready" for the hero. useRankedSurveys (the wall)
  // is capped at 50, so its length reads a flat "50" for anyone at/over the cap.
  // Reuse the existing qualifying-count endpoint (countQualifyingSurveys, uncapped).
  // Perf: it runs one full qualification pass — same cost as the wall the dashboard
  // already loads — but React Query caches it (5-min staleTime), so it's ~one extra
  // pass per member per 5-min window, non-blocking. Until it resolves we fall back
  // to the capped wall length shown as "50+" (honest, never a flat misleading 50).
  const { data: qualifyingData } = useQuery({
    queryKey: ['swipe', 'qualifying-count'],
    queryFn: fetchQualifyingCount,
    staleTime: 5 * 60 * 1000,
  });

  // Phase 2.5 — wall click dispatcher (manual surveys go through
  // /api/manual/start; other exchanges fall through to the existing
  // SurveyStartModal flow).
  async function handleSurveyClick(survey: RankedSurvey) {
    if (survey.exchange !== 'manual') {
      setSelectedSurvey(survey);
      return;
    }
    try {
      const result = await startManualSurvey(survey.id);
      dispatchManualStart(survey.id, result);
    } catch {
      setToast('Something went wrong opening that survey.');
      setTimeout(() => setToast(null), 3000);
    }
  }

  function dispatchManualStart(surveyId: string, result: ManualStartResponse) {
    if (result.kind === 'redirect') {
      window.location.href = result.url;
      return;
    }
    if (result.kind === 'prescreen_required') {
      const customQual = result.unknownQuals.find((q) => q.customQuestion);
      if (customQual?.customQuestion) {
        setManualPrescreen({
          sessionId: result.sessionId,
          surveyId,
          customQuestion: customQual.customQuestion,
        });
        return;
      }
      setToast("This survey isn't ready for you just yet — try another.");
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setToast("This survey isn't a fit right now.");
    setTimeout(() => setToast(null), 3000);
    void refetch();
  }

  async function handleManualPrescreenPass() {
    const surveyId = manualPrescreen?.surveyId;
    setManualPrescreen(null);
    if (!surveyId) return;
    try {
      const result = await startManualSurvey(surveyId);
      dispatchManualStart(surveyId, result);
    } catch {
      setToast('Something went wrong opening that survey.');
      setTimeout(() => setToast(null), 3000);
    }
  }

  function handleManualPrescreenFail() {
    setManualPrescreen(null);
    setToast("This survey isn't a fit right now.");
    setTimeout(() => setToast(null), 3000);
    void refetch();
  }

  function handlePlayGame(game: DisplayGame) {
    if (!game.redirectUrl) return;
    if (isMobileUserAgent()) {
      window.open(game.redirectUrl, '_blank', 'noopener,noreferrer');
    } else {
      setSelectedGame(null);
      setQrGame(game);
    }
  }

  const availablePoints = getAvailablePoints(me);
  const pendingPoints = (me as Record<string, unknown>)?.points_pending as number ?? 0;
  const displayName = getDisplayName(me);

  // First-reward path (T4): show until the member's first redemption. The
  // threshold is the member's effective redemption minimum (config-driven, see
  // T5) so the card tracks any future starter-floor change automatically.
  const { data: pendingSummary } = usePendingSummary();
  const { data: redemptions } = useRedemptions();
  const trustLevel = (me as { trust_level?: number } | undefined)?.trust_level ?? 1;
  const firstRewardThreshold = (me as { redemption_minimum_points?: number } | undefined)?.redemption_minimum_points
    ?? (trustLevel >= 2 ? 500 : 1000);
  const hasRedeemed = (redemptions?.length ?? 0) > 0;
  const showFirstReward = !hasRedeemed && availablePoints < firstRewardThreshold;
  const pointsToFirstReward = Math.max(0, firstRewardThreshold - availablePoints);
  const firstRewardPct = firstRewardThreshold > 0
    ? Math.min(100, Math.round((availablePoints / firstRewardThreshold) * 100))
    : 0;

  // Days until the soonest pending batch releases — drives the hero countdown.
  const firstBatchDays = (() => {
    const entries = pendingSummary?.entries ?? [];
    if (entries.length === 0) return null;
    return Math.min(...entries.map((e) => daysUntil(e.pendingUntil)));
  })();
  const previewSurveys = useMemo(() => scoreSurveys(surveys).slice(0, 2), [surveys]);
  const previewGames = gamesList.slice(0, 2);
  const recentActivity = ledger?.entries.slice(0, 3) ?? [];

  // Hero snapshot stats (display-only; no new fetches).
  //   surveysAvailable — the member's ranked qualified wall (useRankedSurveys).
  //   earnedToday — sum of today's positive ledger deltas. The ledger page is the
  //     20 most-recent entries (DESC), so today's are all captured unless a member
  //     had 20+ earn events in one day (rare); it then undercounts conservatively —
  //     never fabricates. A dedicated "earned today" stat would make it exact.
  const surveysAvailable = surveys.length;
  const WALL_CAP = 50; // exchangeAggregator DEFAULT_LIMIT — surveys.length caps here
  const surveysReadyDisplay = qualifyingData?.count != null
    ? qualifyingData.count.toLocaleString()
    : surveysAvailable >= WALL_CAP ? `${WALL_CAP}+` : String(surveysAvailable);
  const earnedToday = useMemo(() => {
    const entries = ledger?.entries ?? [];
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return entries
      .filter((e) => e.delta > 0 && new Date(e.created_at) >= startOfToday)
      .reduce((sum, e) => sum + e.delta, 0);
  }, [ledger]);

  return (
    <NavShell>
      <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-5">
        {/* Hero — status-rich snapshot, balanced across the width */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/3 translate-x-1/4 pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Left — greeting + available to spend + pending */}
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold font-display">{displayName ? `Welcome back, ${displayName}!` : 'Welcome back!'}</h1>
              <p className="text-purple-200 text-sm mt-3">Available to spend</p>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl sm:text-4xl font-bold tabular-nums tracking-tight">{availablePoints.toLocaleString()} pts</span>
                <span className="text-purple-200 text-sm sm:text-base">= ${pointsToUsd(availablePoints)}</span>
              </div>
              {pendingPoints > 0 && (
                <Link
                  to="/redeem?tab=pending"
                  className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mt-3 text-xs hover:bg-white/25 transition-colors"
                >
                  <Unlock className="w-3.5 h-3.5" />
                  <span className="font-medium tabular-nums">+{pendingPoints.toLocaleString()} pts unlocking</span>
                  {firstBatchDays !== null && (
                    <span className="text-purple-200">
                      &middot; first batch in {firstBatchDays} {firstBatchDays === 1 ? 'day' : 'days'}
                    </span>
                  )}
                </Link>
              )}
            </div>

            {/* Right — snapshot tiles. Wrapped in one translucent panel so they
                read as part of the hero rather than two floating chips. */}
            <div className="grid grid-cols-2 gap-px rounded-2xl bg-white/10 overflow-hidden md:w-[19rem] shrink-0 ring-1 ring-white/10">
              <Link to="/surveys" className="bg-white/5 hover:bg-white/[0.12] transition-colors p-4">
                <span className="flex items-center gap-1.5 text-purple-200 text-xs font-medium"><Zap className="w-3.5 h-3.5" /> Surveys ready</span>
                <span className="block text-2xl font-bold font-display tabular-nums mt-1">{surveysReadyDisplay}</span>
              </Link>
              <Link to="/earning-history" className="bg-white/5 hover:bg-white/[0.12] transition-colors p-4">
                <span className="flex items-center gap-1.5 text-purple-200 text-xs font-medium"><Coins className="w-3.5 h-3.5" /> Earned today</span>
                <span className="block text-2xl font-bold font-display tabular-nums mt-1">+{earnedToday.toLocaleString()}</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Welcome survey — guaranteed first win (B2), shared with /surveys. */}
        {welcomeAvailable && (
          <WelcomeSurveyBanner points={welcome?.points ?? 50} onStart={() => setShowWelcome(true)} />
        )}

        {/* First cash-out progress (T4) — DISTINCT from the welcome bonus above,
            and mutually exclusive with it: shown only once the welcome bonus is
            done (welcomeAvailable=false), so the two "first ..." messages never
            appear together. */}
        {showFirstReward && !welcomeAvailable && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-bold text-[#111827] inline-flex items-center gap-1.5">First cash-out <PartyPopper className="w-4 h-4 text-[#10B981]" /></span>
              <span className="text-xs font-semibold text-[#10B981] tabular-nums">{pointsToFirstReward.toLocaleString()} pts to go</span>
            </div>
            <div className="h-2.5 bg-[#E6F7EE] rounded-full overflow-hidden">
              <div className="h-full bg-[#10B981] rounded-full transition-all" style={{ width: `${firstRewardPct}%` }} />
            </div>
            <p className="text-xs text-[#6B7280] mt-2.5 leading-relaxed">
              Reach <span className="font-semibold text-[#111827]">{firstRewardThreshold.toLocaleString()} pts</span> to cash out your first{' '}
              <span className="font-semibold text-[#111827]">${pointsToUsd(firstRewardThreshold)} gift card</span> — about one more survey. No waiting.
            </p>
          </div>
        )}

        {/* Two-column layout: content left (2/3), sidebar right (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column — main content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Profile completion (arc). Moved into the flow here per the locked
                order: hero → first-reward → profile arc → Swipe → Quick Earns. */}
            <ProfileScoreCard />

            {/* Swipe entry point — training drives better matching, so it sits
                above Quick Earns. Self-hides when the deck is empty. */}
            <SwipeDashboardSection />

            {/* Quick Earns — Surveys */}
            {previewSurveys.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold font-display text-[#111827] flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#F97316]" />
                    Quick Earns
                  </h2>
                  <Link to="/surveys" className="text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1">
                    View all <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {previewSurveys.map((survey) => {
                    const loi = getLoi(survey);
                    const tier = getMatchTier(survey.matchIr, survey.exchange);
                    return (
                      <div
                        key={survey.id}
                        onClick={() => { void handleSurveyClick(survey); }}
                        className="group bg-white rounded-2xl shadow-sm p-5 flex flex-col cursor-pointer hover:shadow-lg transition-all duration-300"
                      >
                        {/* Top: match label (left) + time (right) */}
                        <div className="flex items-center justify-between mb-3">
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${matchTierPillClasses(tier.tone)}`}>
                            {tier.text}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                            <Clock className="w-3.5 h-3.5" /> {loi} min
                          </span>
                        </div>

                        {/* Title — uses pool-generated displayTitle for generic-named surveys */}
                        <h3 className="text-base font-bold text-[#111827] leading-snug mb-2 line-clamp-2 group-hover:text-[#5B4FE8] transition-colors">
                          {survey.displayTitle}
                        </h3>

                        {/* Reward — the visual anchor. L3-L5 see elevated payout
                            with the base struck through + trust-bonus pill. */}
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-2xl font-bold text-[#111827] tabular-nums tracking-tight">${survey.cashValue}</span>
                          <ElevatedPayoutExtras survey={survey} />
                          <span className="text-xs text-[#9CA3AF]">{survey.points_value} pts</span>
                        </div>

                        {/* CTA pinned to bottom */}
                        <div className="mt-auto pt-4">
                          <span className="w-full inline-flex items-center justify-center gap-1 bg-[#5B4FE8] text-white text-sm font-semibold py-2.5 rounded-full group-hover:bg-[#4338CA] transition-colors">
                            Start <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Play & Earn — Games */}
            {previewGames.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold font-display text-[#111827] flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5 text-primary" />
                    Play & Earn
                  </h2>
                  <Link to="/games" className="text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1">
                    View all <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {previewGames.map((game) => (
                    <button
                      key={game.id}
                      type="button"
                      onClick={() => setSelectedGame(game)}
                      className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex text-left"
                    >
                      {game.thumbnailUrl && (
                        <div className="w-24 h-24 flex-shrink-0 bg-gray-100">
                          <img src={game.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-4 flex flex-col flex-1 min-w-0">
                        <h3 className="text-sm font-bold font-display text-[#111827] group-hover:text-primary transition-colors truncate">{game.title}</h3>
                        <p className="text-xs text-[#6B7280] mt-0.5">{game.category}</p>
                        <div className="mt-auto pt-1">
                          <span className="text-xs font-bold text-[#10B981]">Up to {game.maxPoints} pts</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Quick Links */}
            <section>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Link to="/surveys" className="bg-white rounded-2xl shadow-sm p-5 text-center hover:shadow-md transition-all">
                  <ClipboardIcon className="w-6 h-6 text-primary mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-[#111827]">Take Surveys</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Earn points now</p>
                </Link>
                <Link to="/referral" className="bg-white rounded-2xl shadow-sm p-5 text-center hover:shadow-md transition-all">
                  <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-[#111827]">Refer a Friend</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Earn 50 pts each</p>
                </Link>
                <Link to="/redeem" className="bg-white rounded-2xl shadow-sm p-5 text-center hover:shadow-md transition-all">
                  <CircleDollarSign className="w-6 h-6 text-primary mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-[#111827]">Redeem Points</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Cash out rewards</p>
                </Link>
                <Link to="/trust-center" className="bg-white rounded-2xl shadow-sm p-5 text-center hover:shadow-md transition-all">
                  <ShieldIcon className="w-6 h-6 text-primary mx-auto mb-2" />
                  <h3 className="text-sm font-bold text-[#111827]">Trust Center</h3>
                  <p className="text-xs text-[#6B7280] mt-0.5">Verify & earn more</p>
                </Link>
              </div>
            </section>
          </div>

          {/* Right column — sidebar */}
          <div className="lg:col-span-1 space-y-5">
            <StreakWidget
              current={me?.streak_current ?? me?.member?.streak_current ?? 0}
              longest={me?.streak_longest ?? me?.member?.streak_longest ?? 0}
              lastActivityDate={me?.streak_last_activity_date ?? me?.member?.streak_last_activity_date ?? null}
            />

            <div className="bg-white rounded-2xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold font-display text-[#111827] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#6B7280]" />
                  Recent Activity
                </h2>
                <Link
                  to="/earning-history"
                  className="text-sm text-primary font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                >
                  Earnings history
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {recentActivity.length === 0 ? (
                <p className="text-sm text-[#6B7280] text-center py-4">No recent activity yet</p>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                          <Coins className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111827]">{entry.note ?? ledgerLabel(entry.source_type)}</p>
                          <p className="text-xs text-[#6B7280]">{new Date(entry.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium tabular-nums ${entry.delta >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {entry.delta >= 0 ? '+' : ''}{entry.delta}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.main>

      {toast && (
        <div className="fixed bottom-24 sm:bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#111827] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md">
          {toast}
        </div>
      )}

      {selectedSurvey && (
        <SurveyStartModal
          key={selectedSurvey.id}
          survey={selectedSurvey}
          onClose={() => setSelectedSurvey(null)}
          onDeny={() => {
            setSelectedSurvey(null);
            setToast("Got it — we'll find better matches for you");
            void refetch();
            setTimeout(() => setToast(null), 3000);
          }}
          onSurveyUnavailable={() => { setSelectedSurvey(null); void refetch(); }}
          onStarted={() => {
            // Bust the shared ['surveys'] cache so the started survey is gone
            // from the wall when the member returns (same key as SurveysPage).
            void queryClient.invalidateQueries({ queryKey: ['surveys'] });
          }}
          onTryNext={(next) => setSelectedSurvey(next)}
        />
      )}

      {manualPrescreen && (
        <CustomQuestionPrescreenModal
          sessionId={manualPrescreen.sessionId}
          questionHash={manualPrescreen.customQuestion.questionHash}
          question={manualPrescreen.customQuestion.question}
          type={manualPrescreen.customQuestion.type}
          options={manualPrescreen.customQuestion.options}
          required={manualPrescreen.customQuestion.required}
          onPass={() => { void handleManualPrescreenPass(); }}
          onFail={handleManualPrescreenFail}
          onClose={() => setManualPrescreen(null)}
        />
      )}

      {selectedGame && (
        <OfferDetailModal
          item={gameToDetailItem(selectedGame)}
          onClose={() => setSelectedGame(null)}
          onCta={() => handlePlayGame(selectedGame)}
        />
      )}
      {qrGame && (
        <StoreQRModal
          url={qrGame.redirectUrl}
          title={qrGame.title}
          onClose={() => setQrGame(null)}
        />
      )}

      {showWelcome && welcome && (
        <WelcomeSurveyModal
          questions={welcome.questions}
          points={welcome.points}
          onClose={() => setShowWelcome(false)}
          onTakeSurvey={() => {
            setShowWelcome(false);
            void queryClient.invalidateQueries({ queryKey: ['me'] });
            void queryClient.invalidateQueries({ queryKey: ['welcome-survey'] });
            void refetch();
          }}
        />
      )}
    </NavShell>
  );
}
